import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Backend should expose:
 *   GET /api/vehicleHsdFillingWholeReport/print
 * Query params (match ASPX session values):
 *   branch, vehicleCode, vehicleName, regNo, fromDate, toDate, month, year, vehicleMainTypeId, vehicleMode
 */
const API_BASE = "http://nissi-india.com/api/vehicleHsdFillingRpt";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (isoOrDate) => {
  if (!isoOrDate) return "";
  if (typeof isoOrDate === "string" && isoOrDate.includes("/")) return isoOrDate; // already dd/MM/yyyy
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtNum2 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function normalizeRow(x, idx) {
  return {
    sno: idx + 1,

    VehicleMainType: x?.VehicleMainType ?? x?.vehicleMainType ?? "",

    fld_vehicle_code: x?.fld_vehicle_code ?? x?.vehicleCode ?? "",
    fld_vehicle_name: x?.fld_vehicle_name ?? x?.vehicleName ?? "",
    fld_vehicle_regno: x?.fld_vehicle_regno ?? x?.regNo ?? "",

    fld_hsdfillingWorkinhr: fmtNum2(x?.fld_hsdfillingWorkinhr ?? x?.WorkinHr ?? x?.workHr),
    fld_hsdfillinghr: fmtNum2(x?.fld_hsdfillinghr ?? x?.HsdFillingHr ?? x?.hsdHr),

    fld_hsdfillingWorkingKM: fmtNum2(x?.fld_hsdfillingWorkingKM ?? x?.WorkingKM ?? x?.workKm),
    fld_hsdfillingkm: fmtNum2(x?.fld_hsdfillingkm ?? x?.HsdFillingKM ?? x?.hsdKm),

    fld_diselconsumptionhr: fmtNum2(x?.fld_diselconsumptionhr ?? x?.consumptionHr),
    fld_diselconsumptionkm: fmtNum2(x?.fld_diselconsumptionkm ?? x?.consumptionKm),

    cumulativehrs: fmtNum2(x?.cumulativehrs ?? x?.cummulativehrs ?? x?.cumHr),
    cumulativekm: fmtNum2(x?.cumulativekm ?? x?.cummulativekm ?? x?.cumKm),

    fld_progressentrydate: fmtDate(x?.fld_progressentrydate ?? x?.progressDate ?? x?.lastUpdatedDate),
  };
}

export default function VehicleHsdFillingHrKmWholePrint() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  // ✅ read filters like ASPX session values
  const params = useMemo(() => {
    const obj = Object.fromEntries([...sp.entries()]);
    return {
      vehicleMode: obj.vehicleMode || "O", // O / H (Own/Hire)
      branch: obj.branch || "%", // wiseBranch
      vehicleCode: obj.vehicleCode || "%", // wiseVehcode
      vehicleName: obj.vehicleName || "%", // wiseVehname
      regNo: obj.regNo || "%", // wiseRegno
      fromDate: obj.fromDate || "", // wisefromdate (dd/MM/yyyy)
      toDate: obj.toDate || "", // wisetodate (dd/MM/yyyy)
      month: obj.month || "Select", // wisemonth (January/February.. or Select)
      year: obj.year || "Select", // wiseyear
      vehicleMainTypeId: obj.vehicleMainTypeId || "Select", // wisevehicletypee
    };
  }, [sp]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        // ✅ Backend should return:
        // { success:true, rows:[...] }
        const res = await axios.get(`${API_BASE}/list`, { params });
        const d = res?.data || {};
        const list = Array.isArray(d.rows) ? d.rows : Array.isArray(d.data) ? d.data : [];

        if (!alive) return;
        setRows(list);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  const tableRows = useMemo(() => rows.map(normalizeRow), [rows]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 8px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
          .print-landscape { 
            size: A4 landscape;
            margin: 0.25in;
          }
        }
        @page {
          size: A4 landscape;
          margin: 0.25in;
        }
      `}</style>

      <div className="p-2 print:p-1 print-landscape">
        {/* Top Actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>

          <div className="flex gap-2">
            {/* Optional: if you implement backend PDF export */}
            <button
              onClick={() => window.open(`${API_BASE}/exportPdf?${sp.toString()}`, "_blank")}
              className="px-4 py-2 border rounded hover:bg-gray-50"
              title="Optional: backend should return a PDF"
            >
              📄 Export PDF
            </button>

            <button
              onClick={() => window.print()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* Header (match style of your sample template) */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Vehicle HSD Filling HR/KM Report</h1>
              <div className="text-sm font-semibold">CORE CONSTRUCTION</div>
              <div className="text-[10px] mt-1">
                Mode: <b>{params.vehicleMode === "H" ? "Hire" : "Own"}</b> | Branch: <b>{params.branch}</b> | Month/Year:{" "}
                <b>{params.month}</b> / <b>{params.year}</b>
              </div>
              <div className="text-[10px] mt-1">
                From: <b>{params.fromDate || "-"}</b> &nbsp; To: <b>{params.toDate || "-"}</b>
              </div>
            </div>

            <div className="w-44 text-right text-[10px]">
              <div>INFRA SCREEN : B-54/3</div>
              <div>
                Total Rows: <b>{tableRows.length}</b>
              </div>
            </div>
          </div>
        </div>

        {/* Title bar like ASPX */}
        <div className="text-center mb-1 border-2 border-black py-1">
          <h2 className="text-base font-bold">Vehicle HSD Filling HR/KM Whole Print</h2>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 35 }}>S.No</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 130 }}>Vehicle Main Type</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>Vehicle Code</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 150 }}>Vehicle Name</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 120 }}>Vehicle Regno</th>

                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 95 }}>Total Working Hr</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 95 }}>Total HSD Filling (Hr)</th>

                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 95 }}>Total Working KM</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 95 }}>Total HSD Filling (KM)</th>

                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 95 }}>HSD Consump. Hr</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 95 }}>HSD Consump. KM</th>

                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 120 }}>Last Cum Hr</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 120 }}>Last Cum KM</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 110 }}>Last Updated Date</th>
              </tr>
            </thead>

            <tbody>
              {!tableRows?.length ? (
                <tr>
                  <td colSpan={14} className="border border-black px-2 py-2 text-center">
                    No records found.
                  </td>
                </tr>
              ) : (
                tableRows.map((r) => (
                  <tr key={`${r.fld_vehicle_code}-${r.sno}`}>
                    <td className="border border-black px-1 py-1 text-center">{r.sno}</td>
                    <td className="border border-black px-1 py-1">{r.VehicleMainType}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_vehicle_code}</td>
                    <td className="border border-black px-1 py-1">{r.fld_vehicle_name}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_vehicle_regno}</td>

                    <td className="border border-black px-1 py-1 text-right">{r.fld_hsdfillingWorkinhr}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.fld_hsdfillinghr}</td>

                    <td className="border border-black px-1 py-1 text-right">{r.fld_hsdfillingWorkingKM}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.fld_hsdfillingkm}</td>

                    <td className="border border-black px-1 py-1 text-right">{r.fld_diselconsumptionhr}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.fld_diselconsumptionkm}</td>

                    <td className="border border-black px-1 py-1 text-right">{r.cumulativehrs}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.cumulativekm}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_progressentrydate}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer (same template style) */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>

        {/* Bottom Print Button */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
}
